import { useMemo } from 'react';
import { Box, Button, Radio, Tag, Text } from '@nimbus-ds/components';
import { DataList, EmptyMessage } from '@nimbus-ds/patterns';
import { useHistory } from 'react-router';
import { PriceTableDto } from '@tiendanube/common';
import ModalAside from 'App/components/lab/ModalAside';
import { HeaderContent } from 'commons/components';
import { PriceTableInCard } from 'domains/Customers/Customers/pages/CustomerFormPage/components/CustomerPriceTablesCard/CustomerPriceTablesCard';
import { useTranslationCustomers } from 'domains/Customers/hooks';

interface PriceTablesModalProps {
  isOpen: boolean;
  close: () => void;
  priceTables: PriceTableDto[];
  selectedPriceTables: PriceTableInCard[];
  addPriceTable: (priceTable: PriceTableInCard) => void;
  removePriceTable: (id: string) => void;
  addOnlyOnePriceTable: (priceTable: PriceTableInCard) => void;
}

export default function PriceTablesModal({
  isOpen,
  close,
  priceTables,
  selectedPriceTables,
  addOnlyOnePriceTable,
}: Readonly<PriceTablesModalProps>) {
  const { push } = useHistory();
  const t = useTranslationCustomers();
  const tPath = 'customerForm.priceTablesInformation';
  const tPathModal = 'customerForm.priceTablesInformation.modal';

  const selectedPriceTablesObj = useMemo(() => {
    const obj: Record<string, PriceTableInCard> = {};
    for (const priceTable of selectedPriceTables) {
      obj[priceTable.id] = priceTable;
    }
    return obj;
  }, [selectedPriceTables]);

  const goToPriceTables = () => {
    close();
    push('/price-tables');
  };

  const modalContent =
    priceTables.length === 0 ? (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="calc(100vh - 150px)"
      >
        <EmptyMessage
          title={t(`${tPathModal}.emptyState.title`)}
          text={t(`${tPathModal}.emptyState.description`)}
          actions={
            <Button appearance="primary" onClick={goToPriceTables}>
              {t(`${tPathModal}.emptyState.buttonLabel`)}
            </Button>
          }
        />
      </Box>
    ) : (
      <DataList>
        {priceTables.map((priceTable) => (
          <DataList.Row key={priceTable.id}>
            <Box display="flex" gap="2">
              <div>
                <Radio
                  name={`priceTables-${priceTable.id}}`}
                  onChange={() => addOnlyOnePriceTable(priceTable)}
                  checked={!!selectedPriceTablesObj[priceTable.id]}
                  aria-labelledby={`price-table-label-${priceTable.id}`}
                />
              </div>
              <Box display="flex" gap="1" flexDirection="column">
                <Text
                  color="neutral-textHigh"
                  id={`price-table-label-${priceTable.id}`}
                >
                  {priceTable.name}
                </Text>
                <Text fontSize="caption">
                  {priceTable.defaultValue}
                  {t(`${tPathModal}.defaultValueInformation`)}
                </Text>
                <Box display="flex" gap="1">
                  <Tag appearance={priceTable.active ? 'success' : 'neutral'}>
                    {t(
                      `${tPathModal}.${
                        priceTable.active ? 'activeLabel' : 'inactiveLabel'
                      }`,
                    )}
                  </Tag>
                </Box>
              </Box>
            </Box>
          </DataList.Row>
        ))}
      </DataList>
    );

  return (
    <ModalAside
      isOpen={isOpen}
      onDidDismiss={close}
      headerContent={<HeaderContent title={t(`${tPath}.title`)} />}
    >
      {modalContent}
    </ModalAside>
  );
}
